import React, { useCallback, useEffect, useMemo, useState } from "react";
import pageStyles from "../styles/PageLayout.module.css";
import PageHeader from "../shared/PageHeader";
import PageContent from "../shared/PageContent";
import adminApi from "../api/adminApi";
import styles from "../styles/TriggerRecordsPage.module.css";

const DEFAULT_LIMIT = 200;

export function TriggerRecordsPage({ adminToken }) {
  const [kinds, setKinds] = useState([]);
  const [selectedKind, setSelectedKind] = useState("");
  const [refIdLike, setRefIdLike] = useState("");
  const [offset, setOffset] = useState(0);

  const [loadingKinds, setLoadingKinds] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [error, setError] = useState("");

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);

  const selectedMeta = useMemo(
    () => (Array.isArray(kinds) ? kinds.find((k) => k.kind === selectedKind) : null),
    [kinds, selectedKind]
  );

  const fetchKinds = useCallback(async () => {
    if (!adminToken) return;
    setLoadingKinds(true);
    setError("");
    try {
      const res = await adminApi.getTriggerRecordKinds(adminToken);
      const nextKinds = Array.isArray(res.data?.kinds) ? res.data.kinds : [];
      setKinds(nextKinds);
      setSelectedKind((prev) => prev || nextKinds[0]?.kind || "");
    } catch (err) {
      console.error("Failed to load trigger kinds:", err);
      const status = err?.response?.status;
      const details = err?.response?.data?.details || err?.response?.data?.error || err?.message;
      setError(`Не удалось загрузить список триггеров из БД.${status ? ` HTTP ${status}.` : ""}${details ? ` ${String(details)}` : ""}`);
    } finally {
      setLoadingKinds(false);
    }
  }, [adminToken]);

  const fetchRecords = useCallback(async () => {
    if (!adminToken || !selectedKind) return;
    setLoadingRecords(true);
    setError("");
    try {
      const res = await adminApi.getTriggerRecords(adminToken, selectedKind, {
        limit: DEFAULT_LIMIT,
        offset,
        ...(refIdLike.trim() ? { refIdLike: refIdLike.trim() } : {})
      });
      setRecords(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      console.error("Failed to load trigger records:", err);
      const status = err?.response?.status;
      const details = err?.response?.data?.details || err?.response?.data?.error || err?.message;
      setError(`Не удалось загрузить записи триггеров.${status ? ` HTTP ${status}.` : ""}${details ? ` ${String(details)}` : ""}`);
    } finally {
      setLoadingRecords(false);
    }
  }, [adminToken, selectedKind, offset, refIdLike]);

  useEffect(() => {
    fetchKinds();
  }, [fetchKinds]);

  useEffect(() => {
    setOffset(0);
  }, [selectedKind, refIdLike]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(total, offset + records.length);

  return (
    <div className={pageStyles.page}>
      <PageHeader
        title="Триггеры (БД)"
        description="Все записи из bot_triggers: служебные ключи, дедупликации, офферы и состояния."
      />
      <PageContent>
        <div className={styles.layout}>
          <div className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <div className={styles.sidebarTitle}>Kinds</div>
              <button className={styles.button} onClick={fetchKinds} disabled={loadingKinds}>
                Обновить
              </button>
            </div>
            <div className={styles.list}>
              {loadingKinds ? <div className={styles.muted}>Загрузка…</div> : null}
              {!loadingKinds && kinds.length === 0 ? (
                <div className={styles.muted}>Нет данных</div>
              ) : null}
              {kinds.map((item) => (
                <button
                  key={item.kind}
                  className={item.kind === selectedKind ? styles.kindActive : styles.kind}
                  onClick={() => setSelectedKind(item.kind)}
                  title={item.kind}
                >
                  <span className={styles.kindName}>{item.kind}</span>
                  <span className={styles.kindCount}>{Number(item.count || 0)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={styles.main}>
            <div className={styles.controls}>
              <div className={styles.controlRow}>
                <div className={styles.controlTitle}>
                  {selectedKind ? (
                    <>
                      <span className={styles.kindHeadline}>{selectedKind}</span>
                      {selectedMeta ? (
                        <span className={styles.muted}>
                          Обновлялся: {selectedMeta.updated_at ? String(selectedMeta.updated_at) : "—"}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className={styles.muted}>Выберите kind слева</span>
                  )}
                </div>
                <button className={styles.button} onClick={fetchRecords} disabled={loadingRecords || !selectedKind}>
                  Обновить
                </button>
              </div>

              <div className={styles.controlRow}>
                <input
                  className={styles.input}
                  value={refIdLike}
                  onChange={(e) => setRefIdLike(e.target.value)}
                  placeholder="Фильтр по ref_id (contains)…"
                />
                <div className={styles.paging}>
                  <button
                    className={styles.button}
                    onClick={() => setOffset((prev) => Math.max(0, prev - DEFAULT_LIMIT))}
                    disabled={loadingRecords || offset <= 0}
                  >
                    Назад
                  </button>
                  <span className={styles.muted}>
                    {total ? `${pageFrom}–${pageTo} из ${total}` : "—"}
                  </span>
                  <button
                    className={styles.button}
                    onClick={() => setOffset((prev) => prev + DEFAULT_LIMIT)}
                    disabled={loadingRecords || offset + DEFAULT_LIMIT >= total}
                  >
                    Вперёд
                  </button>
                </div>
              </div>
            </div>

            {error ? <div className={styles.error}>{error}</div> : null}

            <div className={styles.records}>
              {loadingRecords ? <div className={styles.muted}>Загрузка…</div> : null}
              {!loadingRecords && selectedKind && records.length === 0 ? (
                <div className={styles.muted}>Записей нет</div>
              ) : null}
              {records.map((row) => (
                <div key={`${row.kind}:${row.ref_id}`} className={styles.record}>
                  <div className={styles.recordHeader}>
                    <span className={styles.recordRef}>{row.ref_id}</span>
                    <span className={styles.muted}>
                      {row.updated_at ? `updated: ${String(row.updated_at)}` : ""}
                    </span>
                  </div>
                  <pre className={styles.recordData}>{JSON.stringify(row.data, null, 2)}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageContent>
    </div>
  );
}

export default TriggerRecordsPage;
